// Persistence rules for an Engagements row: how stored JSON is read back, and
// what may be written. Pure functions, so the rules are testable without the UI.

const BLOBS = { steps: "Onboarding Steps", config: "Finance Config", data: "Finance Data" };

// Parse the three JSON blobs. A blob that is absent is null; a blob that is
// present but unparseable is also null AND its field is read-only, so the
// page never saves its defaults over real data it couldn't read. Finance
// Data edits are computed from the config, so a corrupt config makes Finance
// Data read-only too.
export function parseStored(fields) {
  const out = { steps: null, config: null, data: null, readOnly: [] };
  Object.entries(BLOBS).forEach(([key, field]) => {
    const raw = fields[field];
    if (!raw) return;
    try { out[key] = JSON.parse(raw); } catch (e) { out.readOnly.push(field); }
  });
  if (out.readOnly.includes(BLOBS.config) && !out.readOnly.includes(BLOBS.data)) out.readOnly.push(BLOBS.data);
  return out;
}

// Airtable lastModifiedTime field (all fields) on Engagements. Added 12 Oct 2026
// (fldKxPnTNYdKNrHpe) for the read-before-write guard below.
export const LAST_MODIFIED = "Last Modified";

// One saver per loaded record. Saves run one at a time, in order. Each save
// re-reads the record first and refuses if its Last Modified has moved since
// this page last loaded or saved it: someone else wrote in between, and
// saving now would overwrite their change (last write wins). Once refused,
// the page is stale and every later save is refused until a reload.
// Not a lock: a write landing between the get and the update still wins, and
// Airtable stamps to the second, so two writes in one second look the same.
// save() resolves "saved" | "stale" | "readOnly" and rejects on a proxy error.
export function createSaver({ call, recordId, lastModified, readOnly = [] }) {
  let known = lastModified;
  let stale = false;
  let queue = Promise.resolve();

  const stampOf = (rec) => rec && rec.fields ? rec.fields[LAST_MODIFIED] : undefined;

  const run = async (fields) => {
    if (Object.keys(fields).some(f => readOnly.includes(f))) return "readOnly";
    if (stale) return "stale";
    const current = await call({ action: "get", recordId });
    if (stampOf(current) !== known) { stale = true; return "stale"; }
    const before = stampOf(current);
    const res = await call({ action: "update", recordId, fields });
    // Adopt our own write's stamp. If the response lacks it, or still shows
    // the pre-write value, re-read rather than trust it.
    const echoed = stampOf(res);
    known = echoed !== undefined && echoed !== before ? echoed : stampOf(await call({ action: "get", recordId }));
    return "saved";
  };

  return {
    save(fields) {
      const result = queue.then(() => run(fields));
      queue = result.catch(() => {});
      return result;
    },
    isStale: () => stale,
  };
}

// The Finance Data payload. Only clients with a session block (declared, or
// with sessions or history already stored) carry the sessionBlock key, so
// every other client's Finance Data stays byte-for-byte as it was.
// `extras` (additional work invoiced separately, for clients without a
// ledger) is written only when there is some, for the same reason.
export function buildFinanceData({ hasSessionBlock, months, upcoming, sessions, history, extras }) {
  const payload = { months, upcoming };
  if (extras && extras.length > 0) payload.extras = extras;
  if (hasSessionBlock || (sessions && sessions.length > 0) || history.length > 0) {
    payload.sessionBlock = { sessions: sessions || [] };
    if (history.length > 0) payload.sessionBlock.history = history;
  }
  return payload;
}
